import { useState, useMemo } from "react";
import { EnvData, Log, VariableFact } from "../../src/types";
import useData from "../../src/useData";

export interface LogItem {
  requestId: string;
  timestamp: number;
  result: "allowed" | "denied";
  fact: VariableFact;
}

export interface AuthorizationQuery {
  actor_type: string;
  actor_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
}

const LOG_LIMIT = 5;

export const createEmptyLogRows = (currentLogLength: number) => {
  return Array(LOG_LIMIT - Math.max(currentLogLength, 1))
    .fill(0)
    .map((_v, idx) => idx);
};

export const useAuthorization = (selectedEnv: EnvData, startTime: string) => {
  const [optimisticLog, setOptimisticLog] = useState<{
    [request_id: string]: LogItem;
  }>({});
  const [madeAuthorizationRequest, setMadeAuthorizationRequest] =
    useState(false);

  const { data: serverLog } = useData<Log[]>(
    `/web/environment/${selectedEnv.pub_id}/logs?since=${startTime}`
  );

  const mergedLog = useMemo(() => {
    const optimisticLogs = Object.values(optimisticLog);

    const serverLogs =
      serverLog
        ?.filter(
          (entry) => optimisticLog[entry.fields.request_id] === undefined
        )
        .map(createLogEntryFromServer) ?? [];

    // Sort by timestamp (newest first) and limit to 5
    return [...optimisticLogs, ...serverLogs]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, LOG_LIMIT);
  }, [serverLog, optimisticLog]);

  const authorize = (query: AuthorizationQuery) => {
    (async () => {
      try {
        const { requestId, json } = await attemptAuth(selectedEnv, query);

        if (requestId) {
          setOptimisticLog((prev) => ({
            ...prev,
            [requestId]: createLogEntry({
              requestId,
              timestamp: new Date().getTime(),
              result: json["allowed"] ? "allowed" : "denied",
              query,
            }),
          }));
        }
      } catch (e) {
        console.error(`An unexpected error occurred: ${e}.`);
      } finally {
        setMadeAuthorizationRequest(true);
      }
    })();
  };

  return {
    log: mergedLog,
    authorize,
    madeAuthorizationRequest,
  };
};

async function attemptAuth(env: EnvData, authorizeQuery: AuthorizationQuery) {
  const request = await fetch(`/web/environment/${env.pub_id}/api/authorize/`, {
    method: "POST",
    body: JSON.stringify(authorizeQuery),
  });
  const json = await request.json();
  const requestId = request.headers
    .get("X-Request-Id")
    ?.split(",")
    .slice(-1)[0];

  return {
    requestId,
    json,
  };
}

const createLogEntry = ({
  requestId,
  timestamp,
  result,
  query,
}: {
  requestId: string;
  timestamp: number;
  result: "allowed" | "denied";
  query: AuthorizationQuery;
}): LogItem => ({
  requestId,
  timestamp,
  result,
  fact: {
    predicate: "allow",
    args: [
      {
        kind: "TypedId",
        value: {
          type: query.actor_type,
          id: query.actor_id,
        },
      },
      {
        kind: "TypedId",
        value: {
          type: "String",
          id: query.action,
        },
      },
      {
        kind: "TypedId",
        value: {
          type: query.resource_type,
          id: query.resource_id,
        },
      },
    ],
  },
});

const createLogEntryFromServer = (log: Log): LogItem => ({
  requestId: log.fields.request_id,
  timestamp: new Date(log.timestamp).getTime(),
  result:
    log.fields.result_count !== undefined && log.fields.result_count > 0
      ? "allowed"
      : "denied",
  fact: log.fields.query.fact,
});
